import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocale } from "use-intl";
import { api } from "../api/api";
import type { CreateOrderOutputDto } from "../types/codegen";
import { type ServiceType } from "../types/types";
import { PurchaseStatus, type PaymentResult } from "../payments/types";
import type {
  CheckoutComponent,
  CheckoutError,
  CheckoutPaymentSession,
  CheckoutWebComponentsConfiguration,
  FlowComponent,
} from "../types/checkout";
import { rudderAnalytics, FlightTrackingEvents } from "../rudderstack";
import { isProduction } from "../constants";

declare function CheckoutWebComponents(
  config: CheckoutWebComponentsConfiguration,
): Promise<CheckoutComponent>;

interface Props {
  availabilityToken?: string | null;
  serviceType: ServiceType;
  enabled?: boolean;
  onSubmit?: () => void;
  onSuccess: (result: PaymentResult) => void;
}

export const useCheckoutPayment = ({
  availabilityToken,
  serviceType,
  enabled,
  onSubmit,
  onSuccess,
}: Props) => {
  const locale = useLocale();
  const [flowComponent, setFlowComponent] = useState<FlowComponent | null>(
    null,
  );
  const [loadingForm, setLoadingForm] = useState(enabled);

  const savedOnSuccess = useRef(onSuccess);
  useEffect(() => {
    savedOnSuccess.current = onSuccess;
  }, [onSuccess]);

  const savedOnSubmit = useRef(onSubmit);
  useEffect(() => {
    savedOnSubmit.current = onSubmit;
  }, [onSubmit]);

  const environment = isProduction ? "production" : "sandbox";
  const params = {
    environment,
    serviceType,
    availabilityTokens: [availabilityToken],
  };

  const checkoutSessionQuery = useQuery({
    // TODO: Implement price change recheck before payment
    queryKey: ["checkout-payment", params],
    queryFn: ({ signal }) =>
      api.post<CreateOrderOutputDto>(
        "/orders",
        {
          ...params,
          provider: "CHECKOUT_COM",
        },
        { signal },
      ),
    select: (data) => data.data.data,
    enabled,
  });

  useEffect(() => {
    const renderCheckoutForm = async (session: CheckoutPaymentSession) => {
      setLoadingForm(true);
      const co = await CheckoutWebComponents({
        environment,
        paymentSession: session,
        publicKey: "pk_sbox_ujh4sxadxjdlcnujn376bjrhras",
        locale,
        appearance: {
          colorBorder: "transparent",
          colorFormBorder: "#E7E9EE",
          borderRadius: ["16px", "0"],
          colorAction: "#003e2b",
          colorOutline: "#003e2b",
          subheading: {
            fontFamily:
              'var(--font-sans, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
          },
          label: {
            fontFamily:
              'var(--font-sans, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
          },
          input: {
            fontFamily:
              'var(--font-sans, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
          },
        },
      });
      const flowComponent = co.create("flow", {
        showPayButton: false,
        onSubmit: savedOnSubmit.current,
        onReady: () => {
          setLoadingForm(false);
        },
        onPaymentCompleted: () =>
          savedOnSuccess.current({
            status: PurchaseStatus.SUCCESSFUL,
            orderId: checkoutSessionQuery.data?.checkoutSession?.orderId || "",
          }),
        onError: (_self: FlowComponent, err: CheckoutError) => {
          rudderAnalytics.track(FlightTrackingEvents.USER_UNSUCCESSFULY_PAID, {
            payment_method: "checkout",
            payment_status: "fail",
            transaction_id: checkoutSessionQuery.data?.checkoutSession?.orderId,
          });
          console.error("Error in booking or payment process:", err);
          throw err;
        },
      });
      flowComponent.mount("#flow-container");
      setFlowComponent(flowComponent);
    };

    if (
      checkoutSessionQuery.isSuccess &&
      checkoutSessionQuery.data.checkoutSession?.session &&
      checkoutSessionQuery.data.checkoutSession?.orderId
    ) {
      renderCheckoutForm(checkoutSessionQuery.data.checkoutSession?.session);
    }
  }, [
    checkoutSessionQuery.data?.checkoutSession?.orderId,
    checkoutSessionQuery.data?.checkoutSession?.session,
    checkoutSessionQuery.isSuccess,
    environment,
    locale,
  ]);

  return {
    flowComponent,
    isLoadingForm: loadingForm,
    orderId: checkoutSessionQuery.data?.checkoutSession?.orderId,
  };
};
